import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, ExternalLink, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

// Distance in metres between two lat/lng points
function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function normalizeTitle(t: string): string {
  return t.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
}

interface Item {
  id: string;
  title: string;
  city: string | null;
  sub_city?: string | null;
  latitude: number | null;
  longitude: number | null;
  user_id?: string | null;
  link: string;
  raw: any;
}

function findDuplicateGroups(items: Item[]): Item[][] {
  const groups: Item[][] = [];
  const used = new Set<string>();

  for (let i = 0; i < items.length; i++) {
    if (used.has(items[i].id)) continue;
    const group: Item[] = [items[i]];
    const baseTitle = normalizeTitle(items[i].title);

    for (let j = i + 1; j < items.length; j++) {
      if (used.has(items[j].id)) continue;
      const other = items[j];
      const otherTitle = normalizeTitle(other.title);

      let isDup = false;

      // Coord match (≤ 30m)
      if (
        items[i].latitude != null && items[i].longitude != null &&
        other.latitude != null && other.longitude != null
      ) {
        const dist = distanceMeters(items[i].latitude, items[i].longitude, other.latitude, other.longitude);
        if (dist < 30) isDup = true;
      }

      // Same normalized title in same city
      if (!isDup && baseTitle && baseTitle === otherTitle && items[i].city && items[i].city === other.city) {
        isDup = true;
      }

      if (isDup) {
        group.push(other);
        used.add(other.id);
      }
    }

    if (group.length > 1) {
      used.add(items[i].id);
      groups.push(group);
    }
  }

  return groups;
}

export function DuplicateDetector() {
  const [tab, setTab] = useState<'properties' | 'plots' | 'projects'>('properties');

  const { data: properties, isLoading: lp } = useQuery({
    queryKey: ['dup-properties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title, city, sub_city, latitude, longitude, user_id, approval_status, status')
        .eq('approval_status', 'approved');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: plots, isLoading: lpl } = useQuery({
    queryKey: ['dup-plots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plots')
        .select('id, title, city, sub_city, latitude, longitude, user_id, approval_status, status')
        .eq('approval_status', 'approved');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: projects, isLoading: lpr } = useQuery({
    queryKey: ['dup-projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, city, sub_city, latitude, longitude, approval_status')
        .eq('approval_status', 'approved');
      if (error) throw error;
      return data || [];
    },
  });

  const propertyGroups = useMemo(() => {
    if (!properties) return [];
    return findDuplicateGroups(properties.map(p => ({
      id: p.id, title: getName(p.title), city: p.city, sub_city: p.sub_city,
      latitude: p.latitude as any, longitude: p.longitude as any, user_id: p.user_id,
      link: `/property/${p.id}`, raw: p,
    })));
  }, [properties]);

  const plotGroups = useMemo(() => {
    if (!plots) return [];
    return findDuplicateGroups(plots.map(p => ({
      id: p.id, title: getName(p.title), city: p.city, sub_city: p.sub_city,
      latitude: p.latitude as any, longitude: p.longitude as any, user_id: p.user_id,
      link: `/plot/${p.id}`, raw: p,
    })));
  }, [plots]);

  const projectGroups = useMemo(() => {
    if (!projects) return [];
    return findDuplicateGroups(projects.map(p => ({
      id: p.id, title: getName(p.name), city: p.city, sub_city: p.sub_city,
      latitude: p.latitude as any, longitude: p.longitude as any,
      link: `/projects/${p.id}`, raw: p,
    })));
  }, [projects]);

  const isLoading = lp || lpl || lpr;
  const groups = tab === 'properties' ? propertyGroups : tab === 'plots' ? plotGroups : projectGroups;

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Copy className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-bold">Duplicate Listings Detector</h2>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Surfaces approved listings that appear to be duplicates based on coordinates within 30 m
        or identical normalized titles in the same city.
      </p>

      <Tabs value={tab} onValueChange={v => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="properties">Properties ({propertyGroups.length})</TabsTrigger>
          <TabsTrigger value="plots">Plots ({plotGroups.length})</TabsTrigger>
          <TabsTrigger value="projects">Projects ({projectGroups.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {!groups.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Copy className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No duplicate groups detected. 🎉</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group, idx) => (
            <Card key={idx}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="border-yellow-400 text-yellow-700 bg-yellow-50">
                    {group.length} possible duplicates
                  </Badge>
                  <span className="text-sm text-muted-foreground">in {group[0].city}</span>
                </div>
                <div className="grid gap-2">
                  {group.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.title || '(no title)'}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {item.city}{item.sub_city ? `, ${item.sub_city}` : ''}
                          {item.latitude != null && item.longitude != null && (
                            <span className="ml-1">· {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}</span>
                          )}
                        </div>
                      </div>
                      <Link to={item.link} target="_blank">
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" /> View
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
